import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Buttons,
  FlexContainer,
  Icon,
  ModalContext,
  Separator,
  Spinner,
  Text,
} from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { showModalAction } from 'Store/modules/app/actions';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { createURL } from 'Containers/Plans/utils';
import {
  useAutoRequestPreviousYear,
  useFetchGetPreviousYearResultPolling,
  useFetchGetPreviousYearsPercentage,
} from 'Containers/PreviousYears/hooks';
import { parsePriceFormat } from 'Containers/Result/components/PaymentLater/utils';
import OneColLayout from 'Components/Layouts/OneColLayout';
import { EfirmaInfo } from 'Components/Modal/components';
import { EFIRMA_PREVIOUS_YEAR, PAYMENT_METHOD_PA_MODAL } from 'Constants/modals';
import { routes } from 'Constants/routes';
import { setBodyOverflow } from 'Styles/utils/utils';
import { NOT_EFIRMA_KEY } from 'Constants/elements';
import * as Styled from './styles';

const PreviousYearsFilter = (): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<number>(0);
  const { showModal } = useContext(ModalContext);
  useAutoRequestPreviousYear();
  const { t } = useTranslation(locizeNS.PY);
  const result = useSelector(routeStateSelector);
  const { data, isLoading } = useFetchGetPreviousYearResultPolling();
  const { mutate, isLoading: loading, data: price } = useFetchGetPreviousYearsPercentage();
  const dispatch = useDispatch();

  useEffect(() => {
    if (StorageService.getItem(NOT_EFIRMA_KEY)) {
      history.push(routes.PREVIOUS_YEARS_CHECKOUT);
    }
  }, []);

  useEffect(() => {
    if (data && data?.amountPending !== 0) {
      mutate({ amount: data.amountPending * 100 });
    }
  }, [data]);

  const handleClickEfirma = (): void => {
    setBodyOverflow();
    dispatch(
      showModalAction({
        options: {
          type: EFIRMA_PREVIOUS_YEAR,
        },
      })
    );
  };

  const handleClickNotEfirma = (amount?: number): void => {
    dispatch(
      showModalAction({
        options: {
          type: PAYMENT_METHOD_PA_MODAL,
          data: {
            successUrl: createURL(routes.PREVIOUS_YEARS_WORKING),
            cancelUrl: createURL(routes.PREVIOUS_YEARS),
            upsellingProps: {
              price: {
                amount,
              },
              pendingAmount: data?.amountPending || amount,
            },
          },
        },
      })
    );
  };

  const userAmount = result?.amount || data?.amountPending;
  const haveEfirma = selectedOption === 0;
  const notEfirma = selectedOption === 1;

  const handleRedirect = (): void => {
    if (notEfirma) {
      handleClickNotEfirma(userAmount);
    } else {
      handleClickEfirma();
    }
  };

  const hanldeShowModal = (): void => {
    showModal({
      comp: <EfirmaInfo isPayLater />,
    });
  };

  return (
    <OneColLayout>
      <FlexContainer direction="column" fullHeight>
        {isLoading || loading ? (
          <Spinner size="M" align="center" justify="center" />
        ) : (
          <Styled.Container align="center" direction="column" gap="dimension_16">
            <FlexContainer direction="column" align="center" fullWidth gap="dimension_16">
              <Text type="headline" color="dark" size="XS_L_M">
                {t('PreviousYears.filter.title')}
              </Text>
              <Text type="paragraphs" color="grey" size="S_M_L">
                {t('PreviousYears.filter.subtitle')}
              </Text>
            </FlexContainer>
            <FlexContainer align="center" justify="center" direction="column" gap="dimension_16">
              <Styled.ButtonBox
                checked={notEfirma}
                direction="column"
                align="center"
                fullHeight
                gap={notEfirma ? 'dimension_08' : 'dimension_16'}
                onClick={() => setSelectedOption(1)}
              >
                <Styled.Body direction="column" gap="dimension_08">
                  <FlexContainer gap="dimension_08" direction="column" align="flex-start">
                    <FlexContainer fullWidth justify="space-between" align="center">
                      <Text type="paragraphs" color="dark" size="M_XL_S">
                        {t('PreviousYears.not.efirma.title')}
                      </Text>
                      <FlexContainer direction="column" align="flex-end">
                        <Text type="paragraphs" color="dark" size="M_M_S">
                          {parsePriceFormat(Math.abs(price?.data?.item.discountedPrice || 0))}
                        </Text>
                        <Text type="paragraphs" color="grey" size="XS_M_S">
                          {t('PreviousYears.not.efirma.payNow')}
                        </Text>
                      </FlexContainer>
                    </FlexContainer>
                    <Separator />
                    <Text type="paragraphs" color="dark" size="S_L_M">
                      {t('PreviousYears.not.efirma.info')}
                    </Text>
                    <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                      {t('PreviousYears.not.efirma.first.bullet')}
                    </Styled.BulletParagraph>
                    <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                      {t('PreviousYears.not.efirma.second.bullet')}
                    </Styled.BulletParagraph>
                  </FlexContainer>
                  {notEfirma && (
                    <Styled.Success>
                      <Icon icon="CircleTickSolidXS" color="cold" width={16} height={16} />
                    </Styled.Success>
                  )}
                </Styled.Body>
              </Styled.ButtonBox>
              <Styled.ButtonBox
                checked={haveEfirma}
                direction="column"
                fullHeight
                gap={haveEfirma ? 'dimension_08' : 'dimension_16'}
                onClick={() => setSelectedOption(0)}
              >
                <Styled.Body direction="column" gap="dimension_08">
                  <FlexContainer gap="dimension_08" direction="column" align="flex-start">
                    <FlexContainer fullWidth justify="space-between" align="center">
                      <Text type="paragraphs" color="dark" size="M_XL_S">
                        {t('PreviousYears.have.efirma.title')}
                      </Text>
                      <FlexContainer direction="column" align="flex-end">
                        <Text type="paragraphs" color="dark" size="M_M_S">
                          {parsePriceFormat(Math.abs(price?.data?.item.discountedPrice || 0))}
                        </Text>
                        <Text type="paragraphs" color="grey" size="XS_M_S">
                          {t('PreviousYears.have.efirma.payNow')}
                        </Text>
                      </FlexContainer>
                    </FlexContainer>
                    <Separator />
                    <Text type="paragraphs" color="dark" size="S_L_M">
                      {t('PreviousYears.have.efirma.info')}
                    </Text>
                    <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                      {t('PreviousYears.have.efirma.first.bullet')}
                    </Styled.BulletParagraph>
                    <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                      {t('PreviousYears.have.efirma.second.bullet')}
                    </Styled.BulletParagraph>

                    <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                      {t('PreviousYears.have.efirma.third.bullet')}
                    </Styled.BulletParagraph>
                  </FlexContainer>
                  {haveEfirma && (
                    <Styled.Success>
                      <Icon icon="CircleTickSolidXS" color="cold" width={16} height={16} />
                    </Styled.Success>
                  )}
                </Styled.Body>
              </Styled.ButtonBox>
            </FlexContainer>
            <Text type="paragraphs" color="grey" size="XS_S_S">
              {t('PreviousYears.filter.condition')}
            </Text>
            <Buttons.Tiny label={t('Upselling.tiny.button')} onClick={hanldeShowModal} />
          </Styled.Container>
        )}
        <Styled.ButtonContainer align="center" justify="center">
          <Buttons.Classic
            mode="primary"
            label={t('Efirma.filter.button')}
            size="m"
            onClick={handleRedirect}
          />
        </Styled.ButtonContainer>
      </FlexContainer>
    </OneColLayout>
  );
};

export default PreviousYearsFilter;
